import { Link } from "react-router-dom";
import { ShoppingBag } from "lucide-react";
import { formatIDR } from "@kanata/shared";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { savings } from "@/lib/pdp-pricing";
import { cn } from "@/lib/utils";

interface ProductCardProps {
  /** The harga coret of the variant `fromPriceIdr` is, struck through beside it. */
  compareAtIdr?: number;
  /** A package with an honest saving: tagged Paket Hemat on the photograph. */
  paketHemat?: boolean;
  slug: string;
  name: string;
  tagline?: string;
  image?: string;
  /** Lowest price across the product's variants — what "mulai" refers to. */
  fromPriceIdr: number;
  /** Number of distinct colourways, shown as a hint rather than swatches. */
  colorCount?: number;
  /** Frame colourways, on a product that offers them. Desks only. */
  legColorCount?: number;
  soldOut?: boolean;
  featured?: boolean;
  className?: string;
  /**
   * Opens the quick-add sheet. Absent, the card is a plain link. It is a
   * sibling of the link, never inside it: a button inside an <a> is invalid
   * and screen readers announce the pair as one control.
   */
  onQuickAdd?: () => void;
}

export function ProductCard({
  compareAtIdr,
  paketHemat,
  slug,
  name,
  tagline,
  image,
  fromPriceIdr,
  colorCount,
  legColorCount,
  soldOut,
  featured,
  className,
  onQuickAdd,
}: ProductCardProps) {
  const save = savings({ priceIdr: fromPriceIdr, compareAtIdr });
  return (
    <div
      className={cn(
        "group flex flex-col overflow-hidden rounded-2xl border border-ink/8 bg-white transition-all hover:border-ink/15 hover:shadow-[0_8px_30px_-12px_rgba(22,33,28,0.15)]",
        className,
      )}
    >
      <Link to={`/produk/${slug}`} className="flex flex-1 flex-col">
        <div className="relative aspect-[4/3] overflow-hidden bg-cream">
          {image ? (
            <img
              src={image}
              alt={name}
              loading="lazy"
              className={cn(
                "size-full object-cover transition-transform duration-500 group-hover:scale-[1.03]",
                soldOut && "opacity-55",
              )}
            />
          ) : (
            <div className="size-full bg-brand-tint" />
          )}

          {featured && !soldOut && (
            <Badge className="absolute left-3 top-3">Paling banyak dipilih</Badge>
          )}
          {paketHemat && !featured && !soldOut ? (
            <Badge className="absolute left-3 top-3 bg-sale text-white">Paket Hemat</Badge>
          ) : null}
          {soldOut && (
            <Badge variant="soldOut" className="absolute left-3 top-3 bg-white/90">
              Stok habis
            </Badge>
          )}
          {/* Right, not left: the left corner is the badge's, and a Paket
              Hemat wants both. Rupiah only, as the client asked. */}
          {save && !soldOut && (
            <span className="absolute right-3 top-3 rounded-full bg-sale px-2.5 py-0.5 text-xs font-semibold text-white shadow-sm">
              Hemat {formatIDR(save.amount)}
            </span>
          )}
        </div>

        <div className="flex flex-1 flex-col gap-1 p-5">
          <h3 className="text-lg font-medium text-ink">{name}</h3>
          {tagline && <p className="text-sm leading-snug text-ink/60">{tagline}</p>}

          <div className="mt-auto flex flex-wrap items-baseline gap-x-2 pt-4">
            <span className="text-base font-medium text-ink">{formatIDR(fromPriceIdr)}</span>
            {save && compareAtIdr ? (
              <del className="text-sm text-ink/45">{formatIDR(compareAtIdr)}</del>
            ) : null}
            {colorCount && colorCount > 1 ? (
              // "4 warna" was unambiguous with one axis and is not with two. A
              // desk says both counts; an accessory keeps the plain wording,
              // because "warna meja" on a cable tray is nonsense.
              <span className="text-xs text-ink/45">
                {legColorCount && legColorCount > 1
                  ? `${colorCount} warna meja · ${legColorCount} warna kaki`
                  : `${colorCount} warna`}
              </span>
            ) : null}
          </div>
        </div>
      </Link>
      {onQuickAdd && !soldOut && (
        <div className="px-5 pb-5">
          <Button
            type="button"
            variant="cart"
            size="sm"
            className="w-full"
            onClick={onQuickAdd}
            aria-label={`Tambah ${name} ke keranjang`}
          >
            <ShoppingBag />
            Tambah ke keranjang
          </Button>
        </div>
      )}
    </div>
  );
}
